import * as React from 'react';
import { releaseStepDetailsContainerModuleStyle, antGrey, runwayColors } from '../styles/globalStyles';
import { ToolOutlined, CheckCircleFilled } from '@ant-design/icons';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { dayjs } from '@/extendedDayjs';
import { BetaTestingStyle } from '../../types';
import BetaSoakSettings from '../BetaSoakSettings';
import LiveDurationComponent, { CountStyle } from '../LiveDurationComponent';
import { UpdateBetaStyleRequest } from '../../actions/appSettings';
import Statistic from 'antd/lib/statistic';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import BuildSubmoduleHeader from '../buildModules/BuildSubmoduleHeader';
import { betaStyleString } from '@/components/stepConfigs/stepConfigSettingsForms';

interface BetaSoakModuleProps extends ReleaseComponentProps {
    betaStyle: BetaTestingStyle;
    soakDurationInSeconds: number;
    soakFinished: boolean;
    showSoakTimeRemaining: boolean;
    soakStartedAt?: dayjs.Dayjs;
    showElapsedTime: boolean;
    betaStyleIsUpdating: boolean;
    saveBetaStyle: (input: UpdateBetaStyleRequest) => void;
    betaStyleUpdated: boolean;
}

interface CountDownProps {
    soakStartedAt: Date;
    prescribedDurationInSeconds: number;
    onFinish: () => void;
}

const CountDownComponent = (props: CountDownProps) => {
    const { Countdown } = Statistic;

    const prescribedDuration = dayjs.duration(props.prescribedDurationInSeconds, 'seconds');
    const soakShouldEndAt = dayjs(props.soakStartedAt).add(prescribedDuration).valueOf();

    return (
        <Countdown
            title={
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Text type="secondary" style={{ textAlign: 'center', fontWeight: 600 }}>
                        Time remaining
                    </Text>
                </div>
            }
            value={soakShouldEndAt}
            onFinish={props.onFinish}
            valueStyle={{ fontSize: 30, color: runwayColors.statusYellow, textAlign: 'center' }}
        />
    );
};

const BetaSoakModule = (props: BetaSoakModuleProps): JSX.Element => {
    const [betaSoakSettingsVisible, setBetaSoakSettingsVisible] = React.useState(false);
    const [countdownCompleted, setCoundtownCompleted] = React.useState(false);

    React.useEffect(() => {
        setBetaSoakSettingsVisible(props.betaStyleUpdated ? false : betaSoakSettingsVisible);
    }, [props.betaStyleUpdated]);

    const onBetaStyleChange = (betaStyle: BetaTestingStyle, soakDurationInSeconds?: number) => {
        props.saveBetaStyle({
            orgId: props.orgId,
            betaTestingStyle: betaStyle,
            prescribedBetaSoakDurationSeconds: soakDurationInSeconds,
            appId: props.appId,
        });
    };

    // Show elapsed duration count in yellow unless soak is non-blocking
    const countColor =
        props.betaStyle === BetaTestingStyle.NonBlocking ? runwayColors.statusGreen : runwayColors.statusYellow;

    return (
        <div style={{ ...releaseStepDetailsContainerModuleStyle, backgroundColor: antGrey.grey2, overflow: 'hidden' }}>
            <Space direction="vertical" size={12}>
                <BuildSubmoduleHeader
                    title="Beta soak"
                    ctaTitle="Change beta style"
                    ctaPermissionActionType={RunwayUserAction.UAUpdateAppSettings}
                    ctaButtonProps={{ icon: <ToolOutlined />, onClick: () => setBetaSoakSettingsVisible(true) }}
                />
                <div style={{ display: 'flex' }}>
                    <div style={{ width: '33%' }}>
                        <ul style={{ paddingLeft: 24, paddingTop: 0, paddingBottom: 0, paddingRight: 0 }}>
                            <li>{betaStyleString(props.betaStyle)}</li>
                            {props.betaStyle == BetaTestingStyle.TimeLimitedSoak && (
                                <li>
                                    {`${dayjs
                                        .duration(props.soakDurationInSeconds, 'seconds')
                                        .asHours()
                                        .toFixed(0)} hour soak period`}
                                </li>
                            )}
                        </ul>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', width: '33%' }}>
                        {(props.soakFinished || countdownCompleted) && (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />
                                <Text style={{ fontSize: 18, marginLeft: 12 }}>Soak completed.</Text>
                            </div>
                        )}
                        {props.showSoakTimeRemaining && props.soakStartedAt && countdownCompleted === false && (
                            <CountDownComponent
                                soakStartedAt={props.soakStartedAt.toDate()}
                                prescribedDurationInSeconds={props.soakDurationInSeconds}
                                onFinish={() => {
                                    setCoundtownCompleted(true);
                                }}
                            />
                        )}
                        {props.showElapsedTime && props.soakStartedAt && (
                            <LiveDurationComponent
                                startingAtDate={props.soakStartedAt.toDate()}
                                countStyle={CountStyle.CountUp}
                                countColor={countColor}
                            />
                        )}
                    </div>
                </div>
            </Space>
            <BetaSoakSettings
                open={betaSoakSettingsVisible}
                isLoading={props.betaStyleIsUpdating}
                currentBetaStyle={props.betaStyle}
                prescribedSoakDuration={props.soakDurationInSeconds}
                onChangeBetaStyle={onBetaStyleChange}
                onCancel={() => setBetaSoakSettingsVisible(false)}
            />
        </div>
    );
};

export default BetaSoakModule;
